import React from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import i18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import {
  accentColor,
  bandTeamNoteBg,
  bandTeamNoteBorder,
  bandTeamNoteText,
  dangerColor,
  groupBg,
  groupBorderColor,
  groupCountPillBg,
  groupCountPillText,
  groupRadius,
  textColor,
  textMutedColor,
  toggleOffColor,
  touchTargetMin
} from '../../../style/adminTools';
import {dangerTextOnDark} from '../../../style/adminTenantGrid';
import Pill from '../tenantGrid/Pill';

const t = (key, defaultValue, options) =>
  i18n.t(`adminTenantPage.${key}`, {defaultValue, ...options});

const UNKNOWN = '—';

const Unknown = ({testID}) => (
  <Text
    testID={testID}
    accessibilityLabel={t('unknownValue', 'Unknown')}
    style={{
      fontFamily: fontFamily.font700,
      fontSize: 20,
      color: textMutedColor
    }}
  >
    {UNKNOWN}
  </Text>
);

const formatCountOfLimit = ({count, limit}) => {
  if (limit === null) return count === null ? null : String(count);
  return t('countOfLimit', '{{count}} of {{limit}}', {
    count: count === null ? UNKNOWN : count,
    limit
  });
};

const Tile = ({testID, label, phone, children}) => (
  <View
    testID={testID}
    style={{
      flexBasis: phone ? '47%' : 150,
      flexGrow: 1,
      marginRight: 10,
      marginBottom: 10,
      padding: 12,
      backgroundColor: groupBg,
      borderWidth: 1,
      borderColor: groupBorderColor,
      borderRadius: groupRadius
    }}
  >
    <Text
      style={{
        fontFamily: fontFamily.font600,
        fontSize: 11,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: textMutedColor
      }}
    >
      {label}
    </Text>
    {children}
  </View>
);

const TileValue = ({testID, text, unknown}) =>
  text === null ? (
    <Unknown testID={testID} />
  ) : (
    <Text
      testID={testID}
      style={{
        fontFamily: fontFamily.font700,
        fontSize: 20,
        color: unknown ? textMutedColor : textColor
      }}
    >
      {text}
    </Text>
  );

const UsageBar = ({percent, testID}) => (
  <View
    testID={testID}
    accessibilityRole="progressbar"
    accessibilityValue={{min: 0, max: 100, now: Math.min(percent, 100)}}
    style={{
      height: 6,
      marginTop: 6,
      borderRadius: 3,
      overflow: 'hidden',
      backgroundColor: toggleOffColor
    }}
  >
    <View
      style={{
        width: `${Math.min(percent, 100)}%`,
        height: 6,
        backgroundColor: percent > 100 ? dangerColor : accentColor
      }}
    />
  </View>
);

const HeaderButton = ({label, onPress, testID, danger, phone}) => (
  <TouchableOpacity
    testID={testID}
    accessibilityRole="button"
    onPress={onPress}
    style={{
      minHeight: phone ? touchTargetMin : 36,
      minWidth: touchTargetMin,
      alignSelf: phone ? 'stretch' : undefined,
      marginRight: phone ? 0 : 8,
      marginBottom: 8,
      paddingHorizontal: 14,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 6,
      borderWidth: 2,
      borderColor: danger ? dangerColor : toggleOffColor
    }}
  >
    <Text
      style={{
        fontFamily: fontFamily.font600,
        fontSize: 13,
        color: danger ? dangerColor : textColor
      }}
    >
      {label}
    </Text>
  </TouchableOpacity>
);

const TestTenantPill = ({on, busy, onPress, phone}) => (
  <TouchableOpacity
    testID="tenantPageTestTenant"
    accessibilityRole="switch"
    accessibilityState={{checked: on, busy, disabled: busy}}
    disabled={busy}
    onPress={onPress}
    style={{
      minHeight: phone ? touchTargetMin : 28,
      justifyContent: 'center',
      marginLeft: 8,
      opacity: busy ? 0.6 : 1
    }}
  >
    <View
      style={{
        backgroundColor: groupCountPillBg,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: on ? accentColor : toggleOffColor,
        paddingHorizontal: 10,
        paddingVertical: 3
      }}
    >
      <Text style={groupCountPillText}>
        {on
          ? t('testTenantOn', 'Test tenant: on')
          : t('testTenantOff', 'Test tenant: off')}
      </Text>
    </View>
  </TouchableOpacity>
);

const DeletedPill = () => (
  <View
    testID="tenantPageStatusDeleted"
    style={{
      borderRadius: 12,
      borderWidth: 1,
      borderColor: dangerColor,
      paddingHorizontal: 10,
      paddingVertical: 3
    }}
  >
    <Text
      style={{
        fontFamily: fontFamily.font700,
        fontSize: 11,
        color: dangerTextOnDark
      }}
    >
      {t('statusDeleted', 'Deleted')}
    </Text>
  </View>
);

const NoteCard = ({note}) => (
  <View
    testID="tenantPageNote"
    style={{
      padding: 12,
      marginBottom: 12,
      backgroundColor: groupBg,
      borderWidth: 1,
      borderColor: groupBorderColor,
      borderRadius: groupRadius
    }}
  >
    <Text
      style={{
        fontFamily: fontFamily.font600,
        fontSize: 11,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: textMutedColor
      }}
    >
      {t('note', 'Note')}
    </Text>
    {note === null ? (
      <Unknown testID="tenantPageNoteUnknown" />
    ) : (
      <Text
        testID="tenantPageNoteText"
        style={{
          fontFamily: fontFamily.font500,
          fontSize: 14,
          color: note === '' ? textMutedColor : textColor
        }}
      >
        {note === '' ? t('noNote', 'No note') : note}
      </Text>
    )}
  </View>
);

const TenantHeader = ({
  tenantName,
  model,
  phone,
  testTenantBusy,
  onBackToTenants,
  onToggleTestTenant,
  onDuplicate,
  onDelete,
  onRestore
}) => {
  const {shipments} = model;
  return (
    <View testID="tenantPageHeader">
      <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
        <Text
          testID="tenantPageCrumbRoot"
          style={{
            fontFamily: fontFamily.font500,
            fontSize: 13,
            color: textMutedColor,
            alignSelf: 'center'
          }}
        >
          {`${i18n.t('adminTools.adminToolsTitle')} / `}
        </Text>
        <TouchableOpacity
          testID="tenantPageCrumbTenants"
          accessibilityRole="link"
          onPress={onBackToTenants}
          style={{
            minHeight: phone ? touchTargetMin : undefined,
            justifyContent: 'center'
          }}
        >
          <Text
            style={{
              fontFamily: fontFamily.font500,
              fontSize: 13,
              color: accentColor
            }}
          >
            {i18n.t('adminTenantGrid.title')}
          </Text>
        </TouchableOpacity>
        <Text
          testID="tenantPageCrumbName"
          style={{
            fontFamily: fontFamily.font500,
            fontSize: 13,
            color: textMutedColor,
            alignSelf: 'center'
          }}
        >
          {` / ${tenantName}`}
        </Text>
      </View>
      <Text
        accessibilityRole="header"
        testID="tenantPageName"
        style={{
          fontFamily: fontFamily.font700,
          fontSize: phone ? 24 : 28,
          color: textColor
        }}
      >
        {tenantName}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 12
        }}
      >
        {model.url ? (
          <Text
            testID="tenantPageUrl"
            style={{
              fontFamily: fontFamily.font500,
              fontSize: 13,
              color: textMutedColor,
              marginRight: 8
            }}
          >
            {model.url}
          </Text>
        ) : null}
        {model.deleted ? (
          <DeletedPill />
        ) : (
          <Pill
            testID="tenantPageStatusActive"
            tone="ok"
            text={t('statusActive', 'Active')}
          />
        )}
        {model.testTenant === null ? null : (
          <TestTenantPill
            on={model.testTenant}
            busy={testTenantBusy}
            onPress={onToggleTestTenant}
            phone={phone}
          />
        )}
      </View>
      {model.deleted ? (
        <View
          testID="tenantPageDeletedBanner"
          accessibilityRole="alert"
          style={{
            backgroundColor: bandTeamNoteBg,
            borderColor: bandTeamNoteBorder,
            borderWidth: 1,
            borderRadius: 6,
            padding: 12,
            marginBottom: 12
          }}
        >
          <Text
            style={{
              fontFamily: fontFamily.font500,
              fontSize: 13,
              color: bandTeamNoteText
            }}
          >
            {t(
              'deletedBanner',
              "This tenant is deleted. Its users can't sign in, and its settings can't be edited here until it's restored."
            )}
          </Text>
        </View>
      ) : null}
      <View
        style={{
          flexDirection: phone ? 'column' : 'row',
          flexWrap: 'wrap',
          marginBottom: 4
        }}
      >
        {model.deleted ? (
          model.name !== null && (
            <HeaderButton
              testID="tenantPageRestore"
              label={t('restoreDeletedTenant', 'Restore deleted tenant')}
              onPress={onRestore}
              phone={phone}
            />
          )
        ) : (
          <>
            <HeaderButton
              testID="tenantPageDuplicate"
              label={t('duplicate', 'Duplicate')}
              onPress={onDuplicate}
              phone={phone}
            />
            {model.name !== null && (
              <HeaderButton
                testID="tenantPageDelete"
                label={t('deleteTenant', 'Delete tenant')}
                onPress={onDelete}
                danger
                phone={phone}
              />
            )}
          </>
        )}
      </View>
      <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
        <Tile
          testID="tenantStatPlan"
          label={t('statPlan', 'Plan')}
          phone={phone}
        >
          <TileValue testID="tenantStatPlanValue" text={model.plan} />
        </Tile>
        <Tile
          testID="tenantStatRegularUsers"
          label={i18n.t('tenantSettings.regularUsers')}
          phone={phone}
        >
          <TileValue
            testID="tenantStatRegularUsersValue"
            text={formatCountOfLimit(model.regularUsers)}
            unknown={model.regularUsers.count === null}
          />
        </Tile>
        <Tile
          testID="tenantStatAdminOnlyUsers"
          label={i18n.t('tenantSettings.adminOnlyUsers')}
          phone={phone}
        >
          <TileValue
            testID="tenantStatAdminOnlyUsersValue"
            text={formatCountOfLimit(model.adminOnlyUsers)}
            unknown={model.adminOnlyUsers.count === null}
          />
        </Tile>
        <Tile
          testID="tenantStatShipments"
          label={t('statShipmentsThisMonth', 'Shipments this month')}
          phone={phone}
        >
          <TileValue
            testID="tenantStatShipmentsValue"
            text={formatCountOfLimit(shipments)}
            unknown={shipments.count === null}
          />
          {shipments.percent === null ? null : (
            <UsageBar
              testID="tenantStatShipmentsBar"
              percent={shipments.percent}
            />
          )}
        </Tile>
        <Tile
          testID="tenantStatStores"
          label={t('statStores', 'Stores')}
          phone={phone}
        >
          <TileValue
            testID="tenantStatStoresValue"
            text={model.stores === null ? null : String(model.stores)}
          />
        </Tile>
      </View>
      <NoteCard note={model.note} />
    </View>
  );
};

export default TenantHeader;
